import './App.css';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import SocialSidebar from './components/SocialSidebar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCallback, useEffect, useState, useRef } from 'react';
import SummaryApi from './common';
import Context from './context';
import { useDispatch } from 'react-redux';
import { setUserDetails, viewUserProfile } from './store/userSlice';
import { getCookie } from './common/cookieHelper';
import service from './common/HttpService';
import { trackPageView, trackContact } from './helpers/analyticsHelper';

function App() {
    const dispatch = useDispatch()
    const [cartItems, setCartItems] = useState([])
    const [cartProductCount, setCartProductCount] = useState(0)
    const [wishlistItems, setWishlistItems] = useState([])
    const [wishlistCount, setWishlistCount] = useState(0)
    const [showWishlist, setShowWishlist] = useState(false)
    const [showCart, setShowCart] = useState(false)
    const location = useLocation()

    const isLoginPage = location.pathname === "/login"

    const fetchUserDetails = useCallback(async () => {
        const token = getCookie('token');
        const user_id = getCookie('user_id');
        const user_info = getCookie('user_info');

        if (token && user_id) {
            if (user_info) {
                try {
                    const user = JSON.parse(user_info)
                    dispatch(setUserDetails(user))
                } catch (e) {
                    console.error("Error parsing user_info cookie", e)
                }
            }
            dispatch(viewUserProfile())
        } else {
            try {
                const dataResponse = await fetch(SummaryApi.current_user.url, {
                    method: SummaryApi.current_user.method,
                })
                const dataApi = await dataResponse.json()
                if (dataApi.success) {
                    dispatch(setUserDetails(dataApi.data))
                }
            } catch (error) {
                console.log("Error fetching user details:", error)
            }
        }
    }, [dispatch])

    const fetchUserAddToCart = useCallback(async () => {
        try {
            const user_id = getCookie('user_id') || localStorage.getItem('user_id');
            let fetchedItems = [];

            if (user_id) {
                const response = await service.get(SummaryApi.view_cart.url);

                if (response && (response.success === "true" || response.success === true)) {
                    if (response?.extraData?.cart && Array.isArray(response.extraData.cart)) {
                        fetchedItems = response.extraData.cart;
                    } else {
                        fetchedItems = (Array.isArray(response?.extraData) ? response.extraData : response?.data) || [];
                    }
                }
            }

            setCartItems(fetchedItems)
            const count = fetchedItems.reduce((acc, curr) => acc + (Number(curr.qty || curr.quantity) || 1), 0)
            setCartProductCount(count)
            return fetchedItems;
        } catch (error) {
            console.log("Error fetching cart:", error)
            setCartItems([])
            setCartProductCount(0)
            return [];
        }
    }, [])

    const fetchUserWishlist = useCallback(() => {
        const wishlist = JSON.parse(localStorage.getItem('wishlist')) || []
        setWishlistItems(wishlist)
        setWishlistCount(wishlist.length)
    }, [])



    useEffect(() => {
        fetchUserDetails()
        fetchUserAddToCart()
        fetchUserWishlist()
        // Ensure light mode
        document.documentElement.classList.remove('dark')
        localStorage.removeItem('theme')
    }, [fetchUserDetails, fetchUserAddToCart, fetchUserWishlist])

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [location.pathname])

    // Track PageView on SPA route changes (skip initial mount as index.html base code handles it)
    const isFirstMount = useRef(true)
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            return
        }
        trackPageView(location.pathname + location.search)
    }, [location.pathname, location.search])

    // Global listener for WhatsApp, phone, and email clicks across the application
    useEffect(() => {
        const handleGlobalContactClicks = (e) => {
            const link = e.target.closest('a')
            if (!link) return
            const href = link.getAttribute('href') || ''
            if (href.startsWith('https://wa.me') || href.includes('whatsapp.com')) {
                trackContact({ method: 'WhatsApp', href })
            } else if (href.startsWith('tel:')) {
                trackContact({ method: 'Phone', href })
            } else if (href.startsWith('mailto:')) {
                trackContact({ method: 'Email', href })
            }
        }

        document.addEventListener('click', handleGlobalContactClicks)
        return () => document.removeEventListener('click', handleGlobalContactClicks)
    }, [])



    const [showCheckout, setShowCheckout] = useState(false)
    const [checkoutData, setCheckoutData] = useState(null)
    const [language, setLanguage] = useState(localStorage.getItem('language') || 'English')
    const [languageCode, setLanguageCode] = useState(localStorage.getItem('languageCode') || 'en')
    const [translations, setTranslations] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('translations')) || {};
        } catch (e) {
            return {};
        }
    });
    //  const translateText = useCallback(async (text, targetLang = languageCode) => {
    //     if (!text || targetLang === 'en' || !targetLang) return text;

    //     const cacheKey = `${text}_${targetLang}`;
    //     if (translations[cacheKey]) return translations[cacheKey];

    //     const API_KEY = "AIzaSyCzO261m4XC1SamxVOQPAURKhaauoDEi8c";
    //     try {
    //         const res = await fetch(
    //             `https://translation.googleapis.com/language/translate/v2?key=${API_KEY}`,
    //             {
    //                 method: "POST",
    //                 headers: { "Content-Type": "application/json" },
    //                 body: JSON.stringify({ q: text, target: targetLang }),
    //             }
    //         );
    //         const data = await res.json();
    //         const translated = data?.data?.translations?.[0]?.translatedText;

    //         if (translated) {
    //             setTranslations(prev => {
    //                 const next = { ...prev, [cacheKey]: translated };
    //                 localStorage.setItem('translations', JSON.stringify(next));
    //                 return next;
    //             });
    //             return translated;
    //         }
    //     } catch (error) {
    //         console.error("Translation error:", error);
    //     }
    //     return text;
    // }, [translations, languageCode]);

    // ─── Translation DISABLED (temporarily) ──────────────────────────────────
    // eslint-disable-next-line no-unused-vars
    const translateText = useCallback(async (text) => text, []);
    // eslint-disable-next-line no-unused-vars
    const translateBatch = useCallback(async (texts = []) => texts, []);

    useEffect(() => {
        localStorage.setItem('language', language)
        localStorage.setItem('languageCode', languageCode)
    }, [language, languageCode])

    return (
        <>
            <Context.Provider value={{
                fetchUserDetails,
                cartProductCount,
                setCartProductCount,
                cartItems,
                setCartItems,
                fetchUserAddToCart,
                wishlistItems,
                wishlistCount,
                fetchUserWishlist,
                showWishlist,
                setShowWishlist,
                showCart,
                setShowCart,
                showCheckout,
                setShowCheckout,
                checkoutData,
                setCheckoutData,
                language,
                setLanguage,
                languageCode,
                setLanguageCode,
                translateText,
                translateBatch
            }}>
                <ToastContainer position='top-center' />
                <Header />
                {showCart && <CartDrawer onClose={() => setShowCart(false)} />}
                <main className='min-h-[calc(100vh-160px)] pt-[140px] md:pt-[180px] overflow-hidden'>
                    <Outlet />
                </main>
                {!isLoginPage && <SocialSidebar />}
                {!isLoginPage && <Footer />}
            </Context.Provider >
        </>
    );
}

export default App;
